import type { Dayjs } from 'dayjs'

import { $deliveryZones } from '@/stores/delivery-zone/stores'
import { $isDiscountOptionApplied } from '@/stores/discount-option/computed'
import type { Subscription as PopsSubscription } from '@/types/pops'
import dayjs from '@/utilities/dayjs-config'

import { SubscriptionAddon } from './subscription-addon'

export const NO_SUPPLEMENT_NAME = 'no_addon'

type Address = {
  address1?: string
  address2?: string
  city?: string
  country?: string
  firstName?: string
  lastName?: string
  phone?: string
  province?: string
  zip?: string
  zip5?: string
}

export class Subscription {
  additionalMealSwaps: number
  addons: SubscriptionAddon[]
  billingAddress: Address
  brightbackDiscount: boolean
  creditCard: string
  creditCardType: string
  deliveryNotes?: string
  deliveryWindow: string[]
  deliveryZoneId: number
  discounts: any[]
  endDate: Dayjs | null
  externalSubscriptionId: string
  failedPayment: boolean
  finalPrice: number
  frequency: number
  detoxTea: boolean
  id: number
  image: string
  lastInvoiceDate: Dayjs
  name?: string
  nextBillingDate: Dayjs
  nextWouldBeBillingDate?: Dayjs
  price: number
  productName: string
  quantity: number
  reactivationDate: Dayjs
  renewalDates?: any[]
  reorders: number
  shippingAddress: Address
  shopifyOrderToken?: string
  signUpDate: Dayjs
  sku: string
  startDate: Dayjs
  startDay: string
  status: string
  createdAt: string
  supplement: string
  toBeReactivated: boolean
  totalMealSwaps: number
  totalSkips: number
  totalSubscriptions: number

  constructor(rawSubscription: PopsSubscription) {
    this.additionalMealSwaps = rawSubscription.additional_meal_swaps
    this.addons =
      rawSubscription?.addons?.map(
        (rawSubscriptionAddon) => new SubscriptionAddon(rawSubscriptionAddon),
      ) || []
    this.billingAddress = rawSubscription.billing_address
    this.brightbackDiscount = rawSubscription.brightback_discount
    this.creditCard = rawSubscription.credit_card
    this.creditCardType = rawSubscription.credit_card_type
    this.deliveryNotes = rawSubscription.delivery_notes
    this.deliveryWindow = rawSubscription.delivery_window
    this.deliveryZoneId = rawSubscription.delivery_zone_id
    this.discounts = rawSubscription.discounts
    this.endDate = rawSubscription.end_date
      ? dayjs(rawSubscription.end_date)
      : null
    this.externalSubscriptionId = rawSubscription.external_subscription_id
    this.failedPayment = rawSubscription.failed_payment
    this.finalPrice = rawSubscription.final_price
    this.frequency = rawSubscription.frequency
    this.detoxTea = rawSubscription.detox_tea
    this.id = rawSubscription.id
    this.image = rawSubscription.image
    this.lastInvoiceDate = dayjs(rawSubscription.last_invoice_date)
    this.name = rawSubscription.name
    this.nextBillingDate = dayjs(rawSubscription.next_billing_date)
    this.nextWouldBeBillingDate = dayjs(
      rawSubscription.next_would_be_billing_date,
    )
    this.price = rawSubscription.price
    this.productName = rawSubscription.product_name
    this.quantity = rawSubscription.quantity
    this.reactivationDate = dayjs(rawSubscription.reactivation_date)
    this.renewalDates = rawSubscription.renewal_dates?.map((date) =>
      dayjs(date),
    )
    this.reorders = rawSubscription.reorders
    this.shippingAddress = rawSubscription.shipping_address
    this.shopifyOrderToken = rawSubscription.shopify_order_token
    this.signUpDate = dayjs(rawSubscription.sign_up_date)
    this.sku = rawSubscription.sku
    this.startDate = dayjs(rawSubscription.start_date)
    this.startDay = rawSubscription.start_day
    this.status = rawSubscription.status
    this.createdAt = rawSubscription.created_at
    this.supplement = rawSubscription.supplement
    this.toBeReactivated = rawSubscription.to_be_reactivated
    this.totalMealSwaps = rawSubscription.total_meal_swaps
    this.totalSkips = rawSubscription.total_skips
    this.totalSubscriptions = rawSubscription.total_subscriptions
  }

  get deliveryZone() {
    return $deliveryZones.get()[this.deliveryZoneId]
  }

  /**
   * Total price before discount (if applicable)
   */
  get totalPrice() {
    return this.price
  }

  /**
   * Total price after discount (if applicable)
   */
  get finalTotalPrice() {
    return this.finalPrice
  }

  // TODO: add types
  get fixedAmountDiscount(): number {
    return (
      this.discounts.find((discount) => discount.type === 'fixed_amount')
        ?.amount || 0
    )
  }

  get percentageDiscount() {
    return (
      this.discounts.find((discount) => discount.type === 'percentage')
        ?.amount || 0
    )
  }

  isDiscountOptionApplied(discountOptionId: number) {
    return $isDiscountOptionApplied.get()(this.id, discountOptionId)
  }

  get isCancelled() {
    // TODO: need to ask from POPS team why status not updates after reativatio
    return this.endDate !== null && !this.toBeReactivated
    // return this.status === 'Canceled'
  }

  get isExpired() {
    return this.status === 'Expired'
  }

  get hasDiscount() {
    return !!this.discounts.length
  }

  get hasStartDatePassed() {
    return dayjs().isSameOrAfter(this.startDate, 'day')
  }

  get hasSupplementOrDetoxTea() {
    return this.supplement !== NO_SUPPLEMENT_NAME || this.detoxTea
  }

  get visibleRenewalFrequency() {
    const { frequency: daysCount } = this

    if (daysCount === 30) {
      return 'Monthly subscription'
    } else if (daysCount % 30 === 0) {
      const monthCount = daysCount / 30
      return `${monthCount}-month subscription`
    } else {
      return `${daysCount}-day subscription`
    }
  }
}
